import React, { useState } from 'react';
import ModalBody from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { crudStatus, getPriceTypeInfos, priceTypeEnum, systemConfigKeyEnum } from '../../../../utils/enums';
import { Job } from '../../../../services/apiSLTP/jobs';
import { useCreateJobMutation, useUpdateJobMutation } from '../../../../hooks/mutations/useJobMutation';
import ToggleLine from '../../../ToggleLine';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import DropDown from '../../../DropDown';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ModalProps {
    job: Job | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateJobModal: React.FC<ModalProps> = ({ job, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const { getConfigValue, configs } = useSystemConfig();

    const [designation, setDesignation] = useState(job?.designation || '');
    const [displayOrder, setDisplayOrder] = useState(job?.displayOrder || null);
    const [status, setStatus] = useState(job?.crudStatus || crudStatus.OK);
    const [price, setPrice] = useState(typeof job?.price === 'number' ? job?.price.toString() : '');
    const [priceType, setPriceType] = useState({
        label: capitalizeFirstLetter(t(getPriceTypeInfos(job?.priceType || priceTypeEnum.DAY))),
        value: (job?.priceType || priceTypeEnum.DAY).toString(),
    });
    const [hasTravelZones, setHasTravelZones] = useState(job?.hasTravelZones || false);
    const [hasMealAllowance, setHasMealAllowance] = useState(job?.hasMealAllowance || false);
    const [hasPhoneAllowance, setHasPhoneAllowance] = useState(job?.hasPhoneAllowance || false);

    const { mutate: mutateJob } = useUpdateJobMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating job:', error.message),
    });

    const { mutate: addJob } = useCreateJobMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating job:', error.message),
    });

    const handleOk = () => {
        const jobData = {
            designation,
            displayOrder,
            crudStatus: status,
            priceType: parseInt(priceType.value),
            price: parseFloat(price),
            hasTravelZones,
            hasMealAllowance,
            hasPhoneAllowance,
        };
        if (isEditMode) {
            mutateJob({ jobId: job?._id || '', data: jobData });
        } else {
            addJob(jobData);
        }
    };

    return (
        <ModalBody
            title={t('job')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!designation.length || (displayOrder !== null && displayOrder < 0) || !price.length || !priceType.value.length}
            handleOk={handleOk}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} required />
            <ToggleLine
                checked={status === crudStatus.OK}
                onChange={(isActive) => setStatus(isActive ? crudStatus.OK : crudStatus.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />

            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                required
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    if (value) setPriceType({ value: value?.value, label: value?.label });
                }}
                inputLabel={t('pricing')}
                required
            />
            <ToggleLine
                checked={hasTravelZones}
                onChange={(checked) => setHasTravelZones(checked)}
                title={capitalizeFirstLetter(t('workTravelZones'))}
            />
            <ToggleLine
                checked={hasMealAllowance}
                onChange={(checked) => setHasMealAllowance(checked)}
                title={capitalizeFirstLetter(t('mealAllowance'))}
            />
            <ToggleLine
                checked={hasPhoneAllowance}
                onChange={(checked) => setHasPhoneAllowance(checked)}
                title={capitalizeFirstLetter(t('phoneAllowance'))}
            />
            <CustomTextInput
                value={displayOrder?.toString() || ''}
                type="number"
                label={t('displayOrder')}
                onChange={(value) => setDisplayOrder(parseInt(value) ?? null)}
            />
        </ModalBody>
    );
};

export default UpdateJobModal;
